/* Isolated QA catalog: one reviewed scope drives cards, prices and facet counts. */
class TlsGoldCatalog extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    if (this.normalizeLegacyUrl()) return;
    this.ready = true;
    this.grid = this.querySelector('#product-grid');
    this.panel = this.querySelector('.gold-filter-panel');
    this.form = this.querySelector('form');
    this.sort = this.querySelector('#SortBy');
    this.mobileSort = this.querySelector('#SortByMobile');
    this.modalObservers = [];
    for (const card of this.querySelectorAll('[data-gold-card]')) {
      const modal = document.getElementById(`QuickAdd-${card.dataset.productId}`);
      if (!modal) continue;
      const syncVariantLink = () => {
        const variantId = modal.querySelector('product-form input[name="id"]')?.value
          || modal.querySelector('form[action*="/cart/add"] input[name="id"]')?.value;
        const link = modal.querySelector('a.product__view-details');
        if (!variantId || !link) return;
        const url = new URL(link.href, location.origin);
        url.searchParams.set('variant', variantId);
        if (link.href !== url.href) link.href = url.href;
      };
      const observer = new MutationObserver(syncVariantLink);
      observer.observe(modal, { childList: true, subtree: true, attributes: true, attributeFilter: ['value','open'] });
      modal.addEventListener('change', syncVariantLink);
      this.modalObservers.push({ modal, observer, syncVariantLink });
    }
    for (const option of this.sort.options) this.mobileSort.append(option.cloneNode(true));
    this.mobileSort.addEventListener('change', () => { this.sort.value = this.mobileSort.value; this.render(true); });
    this.items = [...this.querySelectorAll('[data-gold-card]')].map((node, rank) => ({
      node, rank, manualRank: Number(node.dataset.manualRank || rank), title: node.dataset.title, price: Number(node.dataset.price) / 100,
      created: Number(node.dataset.created), available: node.dataset.available === 'true' ? '1' : '0',
      mount: node.dataset.mount || '', dimmable: node.dataset.dimmable || ''
    }));
    this.groups = [
      { key: 'tls_available', field: 'available', title: 'Availability', values: ['1', '0'], label: v => v === '1' ? 'In stock' : 'Out of stock' },
      { key: 'tls_mount', field: 'mount', title: 'Mounting Type' },
      { key: 'tls_dimmable', field: 'dimmable', title: 'Dimmable', label: v => v === 'false' ? 'No' : v === 'true' ? 'Yes' : v }
    ];
    const host = this.querySelector('[data-gold-filter-groups]');
    for (const group of this.groups) {
      group.values ||= [...new Set(this.items.map(x => x[group.field]).filter(Boolean))].sort();
      if (!group.values.length) continue;
      const fieldset = document.createElement('fieldset');
      const details = document.createElement('details'); details.className = 'gold-facet-disclosure';
      const summary = document.createElement('summary'); summary.className = 'facets__summary caption-large focus-offset'; summary.textContent = group.title; details.append(summary);
      const legend = document.createElement('legend'); legend.className = 'visually-hidden'; legend.textContent = group.title; fieldset.append(legend);
      for (const value of group.values) {
        const label = document.createElement('label');
        const input = document.createElement('input'); input.type = 'checkbox'; input.name = group.key; input.value = value;
        const text = document.createElement('span'); label.append(input, text); fieldset.append(label);
      }
      details.append(fieldset); host.append(details);
    }
    if (Number(this.dataset.sourcePages) > 1) {
      this.querySelector('.gold-catalog-toolbar').hidden = true;
      this.panel.hidden = true;
      return;
    }
    this.readUrl();
    this.form.addEventListener('submit', event => { event.preventDefault(); this.close(); });
    this.form.addEventListener('input', () => this.render(true));
    this.sort.addEventListener('change', () => this.render(true));
    this.querySelector('.gold-filter-open').addEventListener('click', () => this.open());
    this.querySelector('.gold-filter-close').addEventListener('click', () => this.close());
    this.querySelector('.gold-apply').addEventListener('click', () => { this.render(true); this.close(); });
    this.querySelector('.gold-backdrop').addEventListener('click', () => this.close());
    this.querySelectorAll('.gold-clear').forEach(button => button.addEventListener('click', () => {
      this.form.querySelectorAll('.gold-url-filter').forEach(input => input.remove());
      for (const input of this.form.elements) {
        if (input.type === 'checkbox') input.checked = false;
        else if (input.name) input.value = '';
      }
      this.render(true);
    }));
    this.onPop = () => { this.readUrl(); this.render(false); };
    this.onKey = event => {
      if (!this.panel.classList.contains('is-open')) return;
      if (event.key === 'Escape') { event.preventDefault(); this.close(); }
      if (event.key === 'Tab') {
        const nodes = [...this.panel.querySelectorAll('button,input,select,summary')].filter(x => !x.disabled && x.getClientRects().length);
        const first = nodes[0], last = nodes.at(-1);
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener('popstate', this.onPop);
    document.addEventListener('keydown', this.onKey);
    this.onResize = () => { if (innerWidth >= 750 && this.panel.classList.contains('is-open')) this.close(); };
    window.addEventListener('resize', this.onResize);
    this.render(false);
  }
  disconnectedCallback() {
    for (const { modal, observer, syncVariantLink } of this.modalObservers || []) {
      observer.disconnect();
      modal.removeEventListener('change', syncVariantLink);
    }
    window.removeEventListener('popstate', this.onPop);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('keydown', this.onKey);
    if (this.panel?.classList.contains('is-open')) this.close();
  }
  normalizeLegacyUrl() {
    const url = new URL(location.href);
    const legacy = [...url.searchParams.entries()].filter(([key]) => key.startsWith('filter.'));
    if (!legacy.length) return false;
    const aliases = {
      'filter.v.availability': 'tls_available',
      'filter.v.price.gte': 'tls_min', 'filter.v.price.lte': 'tls_max',
      'filter.p.m.custom.mounting_type': 'tls_mount',
      'filter.v.m.custom.variant_dimmable': 'tls_dimmable'
    };
    const explicit = new Set([...url.searchParams.keys()].filter(key => key.startsWith('tls_')));
    let unsupported = false;
    for (const [key, raw] of legacy) {
      const alias = aliases[key];
      if (!alias) { unsupported = true; continue; }
      if (explicit.has(alias)) continue;
      if (alias === 'tls_min' || alias === 'tls_max') {
        if (raw !== '' && Number.isFinite(Number(raw)) && Number(raw) >= 0) url.searchParams.set(alias, raw);
        else if (raw !== '') unsupported = true;
      } else {
        for (const value of raw.split(',').filter(Boolean)) {
          const valid = alias === 'tls_available' ? ['0','1'].includes(value)
            : alias === 'tls_dimmable' ? ['true','false'].includes(value) : true;
          if (valid) url.searchParams.append(alias,value); else unsupported = true;
        }
      }
    }
    for (const [key] of legacy) url.searchParams.delete(key);
    url.searchParams.delete('page');
    if (unsupported) url.searchParams.set('tls_unsupported','1');
    if (this.dataset.previewTheme) url.searchParams.set('preview_theme_id', this.dataset.previewTheme);
        else url.searchParams.delete('preview_theme_id');
    window.location.replace(url.href);
    return true;
  }
  readUrl() {
    const params = new URLSearchParams(location.search);
    this.form.querySelectorAll('.gold-url-filter').forEach(input => input.remove());
    for (const input of this.form.elements) {
      if (!input.name) continue;
      if (input.type === 'checkbox') input.checked = params.getAll(input.name).flatMap(x => x.split(',')).includes(input.value);
      else input.value = params.get(input.name) || '';
    }
    // Keep valid URL selections even when no current product supplies that facet value.
    for (const group of this.groups) {
      for (const value of params.getAll(group.key).flatMap(raw => raw.split(',')).filter(Boolean)) {
        if ([...this.form.elements].some(input => input.name === group.key && input.value === value)) continue;
        const input = document.createElement('input');
        input.type = 'hidden'; input.className = 'gold-url-filter'; input.name = group.key; input.value = value;
        this.form.append(input);
      }
    }
    const sort = params.get('tls_sort') || params.get('sort_by') || 'best-selling';
    this.sort.value = [...this.sort.options].some(x => x.value === sort) ? sort : 'best-selling';
  }
  state() {
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(this.form)) if (value !== '') params.append(key, value);
    return params;
  }
  matches(item, params, omit) {
    for (const group of this.groups) {
      if (group.key === omit) continue;
      const values = params.getAll(group.key);
      if (values.length && !values.includes(item[group.field])) return false;
    }
    const min = params.get('tls_min'), max = params.get('tls_max');
    if (min !== '' && min !== null && item.price < Number(min)) return false;
    if (max !== '' && max !== null && item.price > Number(max)) return false;
    return true;
  }
  render(updateUrl) {
    const params = this.state(), sort = this.sort.value;
    this.querySelector('.gold-legacy-notice').hidden = updateUrl || !new URLSearchParams(location.search).has('tls_unsupported');
    this.mobileSort.value = sort;
    const visible = this.items.filter(item => this.matches(item, params));
    const compare = {
      'manual': (a,b) => a.rank-b.rank,
      'price-ascending': (a,b) => a.price-b.price,
      'price-descending': (a,b) => b.price-a.price,
      'title-ascending': (a,b) => a.title.localeCompare(b.title),
      'title-descending': (a,b) => b.title.localeCompare(a.title),
      'created-ascending': (a,b) => a.created-b.created,
      'created-descending': (a,b) => b.created-a.created
    }[sort] || ((a,b) => a.rank-b.rank);
    visible.sort((a,b) => compare(a,b) || a.rank-b.rank);
    for (const item of this.items) {
      item.node.hidden = !visible.includes(item);
      item.node.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
    }
    for (const item of visible) this.grid.append(item.node);
    const total = this.items.length;
    this.querySelector('#ProductCountDesktop').textContent = visible.length === total ? `${total} products` : `${visible.length} of ${total} products`;
    this.querySelector('.gold-empty').hidden = visible.length > 0;
    this.querySelector('.gold-filter-form > .gold-clear').hidden = ![...params.values()].some(Boolean);
    for (const group of this.groups) {
      const candidates = this.items.filter(item => this.matches(item, params, group.key));
      for (const input of this.form.querySelectorAll('input[type=checkbox]')) {
        if (input.name !== group.key) continue;
        const count = candidates.filter(item => item[group.field] === input.value).length;
        const text = `${group.label ? group.label(input.value) : input.value} (${count})`;
        input.nextElementSibling.textContent = text;
        input.disabled = !count && !input.checked;
      }
    }
    const chips = this.querySelector('.gold-active-filters'); chips.replaceChildren();
    for (const [key,value] of params) {
      if (!value) continue;
      const group = this.groups.find(x => x.key === key);
      const button = document.createElement('button'); button.type = 'button';
      button.textContent = `${group?.title || (key === 'tls_min' ? 'Price from' : 'Price to')}: ${group?.label ? group.label(value) : value} ×`;
      button.addEventListener('click', () => {
        for (const input of this.form.elements) if (input.name === key && (input.type !== 'checkbox' || input.value === value)) {
          if (input.type === 'checkbox') input.checked = false; else input.value = '';
        }
        this.render(true);
      });
      chips.append(button);
    }
    if (updateUrl) {
      const url = new URL(location.href);
      for (const key of [...url.searchParams.keys()]) if (key.startsWith('tls_') || key.startsWith('filter.') || key === 'sort_by' || key === 'page') url.searchParams.delete(key);
      for (const [key,value] of params) if (value) url.searchParams.append(key,value);
      url.searchParams.set('tls_sort', sort);
      const sourceSort = this.dataset.sourceSort || 'best-selling';
      if ((sort === 'manual' || sort === 'best-selling') && sourceSort !== sort) {
        url.searchParams.set('sort_by', sort);
        if (this.dataset.previewTheme) url.searchParams.set('preview_theme_id', this.dataset.previewTheme);
        else url.searchParams.delete('preview_theme_id');
        window.location.assign(url.href);
        return;
      }
      if (sourceSort === 'manual' || sourceSort === 'best-selling') url.searchParams.set('sort_by', sourceSort);
      history.pushState({}, '', url);
    }
  }
  open() {
    this.savedOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.panel.classList.add('is-open'); this.panel.setAttribute('role','dialog'); this.panel.setAttribute('aria-modal','true');
    this.querySelector('.gold-backdrop').classList.add('is-open');
    this.querySelector('.gold-filter-open').setAttribute('aria-expanded','true');
    this.querySelector('.gold-filter-close').focus();
  }
  close() {
    this.panel.classList.remove('is-open'); this.panel.removeAttribute('role'); this.panel.removeAttribute('aria-modal');
    this.querySelector('.gold-backdrop').classList.remove('is-open');
    document.body.style.overflow = this.savedOverflow || '';
    this.querySelector('.gold-filter-open').setAttribute('aria-expanded','false');
    if (innerWidth < 750) this.querySelector('.gold-filter-open').focus();
  }
}
if (!customElements.get('tls-gold-catalog')) customElements.define('tls-gold-catalog', TlsGoldCatalog);